/**
 * TrackReadinessSection
 *
 * Displays the readiness definition inheritance chain for a track's Defaults
 * tab, using the same visual pattern as TrackSessionDefaultsSection:
 *   - A single dropdown row (Readiness check) with Inherit / override options
 *   - A collapsible Workspace → Cycle → Track → Session → Used Now chain
 *   - A criteria summary strip below when a definition is active
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Info, ChevronDown, ChevronUp, Tag, AlignLeft, Bot } from 'lucide-react';
import { SessionDefaultsInheritanceChainRows } from './SessionDefaultsInheritanceChain';
import type { SessionDefaultLayerSnapshot } from './SessionDefaultsInheritanceChain';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { useAttributesStore } from '@/stores/prioritization/attributesStore';
import { useReadinessDefinitionStore } from '@/stores/prioritization/readinessDefinitionStore';
import { resolveEffectiveReadinessDefinitionId } from '@/lib/readinessDefinitionResolver';
import type { Track } from '@/types/prioritization/track';
import type { DegapProcess } from '@/types/prioritization/degapCycle';
import type { ReadinessCriterion } from '@/types/prioritization/readinessDefinition';
import { Select } from '@/components/ds';

export interface TrackReadinessSectionProps {
  track: Track;
  cycle: DegapProcess | null;
}

// ─── Criterion pill ───────────────────────────────────────────────────────────

function CriterionPill({
  criterion,
  getAttributeByKey,
}: {
  criterion: ReadinessCriterion;
  getAttributeByKey: (key: string) => { name: string } | undefined;
}) {
  let label = '';
  let Icon = AlignLeft;

  if (criterion.type === 'attribute_values') {
    label = criterion.label ?? getAttributeByKey(criterion.attributeKey)?.name ?? criterion.attributeKey;
    Icon = Tag;
  } else if (criterion.type === 'field_populated') {
    label = criterion.label ?? criterion.fieldKey;
    Icon = AlignLeft;
  } else if (criterion.type === 'agent_signoff') {
    label = criterion.label ?? criterion.agentKey;
    Icon = Bot;
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border border-gray-200 dark:border-[var(--rule)] bg-white dark:bg-[var(--surface)] text-gray-700 dark:text-[var(--text-pri)]">
      <Icon className="h-3 w-3 text-gray-400 dark:text-[var(--text-sec)] shrink-0" />
      {label}
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function TrackReadinessSection({ track, cycle }: TrackReadinessSectionProps) {
  const updateTrack = useTrackStore((s) => s.updateTrack);
  const getCurrentWorkspace = useTenantStore((s) => s.getCurrentWorkspace);
  const getAttributeByKey = useAttributesStore((s) => s.getAttributeByKey);
  const [busy, setBusy] = useState(false);
  const [showDetails, setShowDetails] = useState(true);

  const rdStore = useReadinessDefinitionStore();
  useEffect(() => {
    const ws = getCurrentWorkspace();
    if (ws?.id && rdStore.definitions.length === 0 && !rdStore.loading) {
      rdStore.loadForWorkspace(ws.id);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const wsDefId: string | null = rdStore.workspaceDefinitionId;
  const cycleDefId: string | null = cycle?.readinessDefinitionId ?? null;
  const trackDefId: string | null = track.readinessDefinitionId ?? null;

  const effectiveReadinessId = useMemo(
    () => resolveEffectiveReadinessDefinitionId(wsDefId, cycleDefId, trackDefId, null),
    [wsDefId, cycleDefId, trackDefId],
  );

  const readinessLayer: SessionDefaultLayerSnapshot = useMemo(() => {
    const defName = (id: string | null): string => {
      if (!id) return '—';
      return rdStore.definitions.find((d) => d.id === id)?.name ?? '—';
    };
    const rawSource = effectiveReadinessId?.source ?? null;
    const source: 'global' | 'cycle' | 'track' | 'session' =
      rawSource === 'workspace' ? 'global' : (rawSource ?? 'global') as 'global' | 'cycle' | 'track' | 'session';

    return {
      global: defName(wsDefId),
      cycle: defName(cycleDefId),
      track: defName(trackDefId),
      session: '—',
      effective: defName(effectiveReadinessId?.id ?? null),
      source,
    };
  }, [effectiveReadinessId, wsDefId, cycleDefId, trackDefId, rdStore.definitions]);

  const effectiveDefinition = rdStore.definitions.find((d) => d.id === effectiveReadinessId?.id) ?? null;
  const trackReadinessValue = trackDefId ?? '';
  const isOverridden = !!trackReadinessValue;

  const handleChange = useCallback(
    async (value: string) => {
      setBusy(true);
      try {
        await updateTrack(track.id, { readinessDefinitionId: value === '' ? null : value });
      } finally {
        setBusy(false);
      }
    },
    [track.id, updateTrack],
  );

  return (
    <div className="rounded-lg border border-gray-200 dark:border-[var(--rule)] bg-white dark:bg-[var(--surface-raised)] overflow-hidden">
      {/* Header — matches TrackSessionDefaultsSection */}
      <div className="px-4 py-3 flex items-center justify-between border-b border-gray-100 dark:border-[var(--rule)]">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-[var(--text-pri)] flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-gray-400 dark:text-[var(--text-sec)]" />
          Readiness
        </h3>
        <span className="text-xs text-gray-500 dark:text-[var(--text-sec)]">Inherited unless overridden</span>
      </div>

      {/* Single-row selector — matches the session defaults row pattern */}
      <div className="divide-y divide-gray-100 dark:divide-[var(--rule)]">
        <div className="px-4 py-2.5 flex items-center gap-4">
          <span className="text-sm font-medium text-gray-700 dark:text-[var(--text-sec)] w-28 shrink-0">
            Readiness check
          </span>

          <Select
            value={trackReadinessValue}
            onChange={(e) => void handleChange(e.target.value)}
            disabled={busy}
            className={`text-sm rounded border px-2.5 py-1.5 min-w-[180px] focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-[var(--surface)] dark:text-[var(--text-pri)] ${
              isOverridden
                ? 'border-blue-300 bg-blue-50/50 text-gray-900 dark:border-blue-500/50 dark:bg-blue-900/20'
                : 'border-gray-200 bg-gray-50 text-gray-700 dark:border-[var(--rule)] dark:bg-[var(--surface)]'
            }`}
          >
            <option value="">Inherit</option>
            {rdStore.definitions.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </Select>

          {!isOverridden && readinessLayer.effective !== '—' && (
            <span className="flex items-center gap-1.5 min-w-0">
              <span className="text-sm font-medium text-gray-700 dark:text-[var(--text-pri)] truncate">
                {readinessLayer.effective}
              </span>
              <span className="text-xs text-gray-400 dark:text-[var(--text-sec)] shrink-0">
                via {readinessLayer.source}
              </span>
            </span>
          )}
        </div>
      </div>

      {/* Collapsible inheritance chain — same as TrackSessionDefaultsSection */}
      <div className="border-t border-gray-100 dark:border-[var(--rule)]">
        <button
          type="button"
          onClick={() => setShowDetails(!showDetails)}
          className="w-full px-4 py-2 flex items-center gap-1.5 text-xs text-gray-500 dark:text-[var(--text-sec)] hover:text-gray-700 dark:hover:text-[var(--text-pri)] hover:bg-gray-50 dark:hover:bg-[var(--hover-tint)] transition-colors"
        >
          <Info className="h-3 w-3" />
          Inheritance chain
          {showDetails ? <ChevronUp className="h-3 w-3 ml-auto" /> : <ChevronDown className="h-3 w-3 ml-auto" />}
        </button>
        {showDetails && (
          <div className="px-4 pb-4 pt-0.5">
            <SessionDefaultsInheritanceChainRows
              depth="track"
              rows={[{ key: 'readiness', label: 'Readiness check', layer: readinessLayer }]}
            />
          </div>
        )}
      </div>

      {/* Active criteria strip — only shown when a definition is active */}
      {effectiveDefinition && effectiveDefinition.criteria.length > 0 && (
        <div className="border-t border-gray-100 dark:border-[var(--rule)] px-4 py-3 bg-gray-50/50 dark:bg-[var(--surface)]">
          <p className="text-xs font-semibold text-gray-600 dark:text-[var(--text-sec)] uppercase tracking-wide mb-2">
            {effectiveDefinition.criteria.length} {effectiveDefinition.criteria.length === 1 ? 'criterion' : 'criteria'} required
          </p>
          <div className="flex flex-wrap gap-1.5">
            {effectiveDefinition.criteria.map((c, i) => (
              <CriterionPill key={i} criterion={c} getAttributeByKey={getAttributeByKey} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default TrackReadinessSection;
